import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

// Logo oficial Kairós
const LOGO_URL = "/logo-kairos.png";

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const LoginPage = () => {
  const { user, loading, setUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erro ao fazer login');
      }

      // Login bem sucedido
      setUser({
        user_id: data.user_id,
        email: data.email,
        name: data.name,
        picture: data.picture,
      });
    } catch (err) {
      setError(err.message || 'Credenciais inválidas');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-kairos-orange border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-kairos-orange/15 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '4s' }}></div>
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-kairos-orange/10 rounded-full blur-[100px] animate-pulse" style={{ animationDuration: '6s' }}></div>
      </div>

      <div className="relative z-10 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-block mb-6">
            <img src={LOGO_URL} alt="Kairós" className="w-20 h-20 rounded-full object-cover mx-auto shadow-lg shadow-kairos-orange/20" />
          </div>
          <h1 className="font-display text-3xl font-bold text-white mb-2">Kairós</h1>
          <p className="text-gray-400">Encontro de Casais</p>
        </div>

        {/* Login Card */}
        <div className="bg-[var(--ds-surface)] border border-[var(--ds-border)] rounded-2xl p-8">
          <h2 className="font-display text-2xl text-white text-center mb-2">Área Administrativa</h2>
          <p className="text-gray-400 text-center mb-8 text-sm">
            Entre com suas credenciais para acessar o painel
          </p>

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@email.com"
                  required
                  data-testid="email-input"
                  className="w-full h-12 pl-12 pr-4 bg-[var(--ds-background)] border border-[var(--ds-border)] rounded-lg text-white placeholder:text-gray-600 focus:outline-none focus:border-kairos-orange/50 transition-colors"
                />
              </div>
            </div>

            {/* Senha */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">Senha</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                  className="w-full h-12 pl-12 pr-12 bg-[var(--ds-background)] border border-[var(--ds-border)] rounded-lg text-white placeholder:text-gray-600 focus:outline-none focus:border-kairos-orange/50 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Esqueceu a senha */}
            <div className="text-right">
              <Link 
                to="/admin/forgot-password" 
                className="text-kairos-orange hover:text-kairos-orange-light text-sm transition-colors"
              >
                Esqueceu a senha?
              </Link>
            </div>

            {/* Botão de Login */}
            <Button
              type="submit"
              disabled={isLoading}
              data-testid="login-btn"
              className="w-full h-12 bg-gradient-to-r from-kairos-orange to-kairos-orange-light text-white font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(255,107,0,0.3)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                'Entrar'
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-gray-500 text-sm mt-8">
          Acesso restrito a administradores
        </p>

        {/* Créditos Groover Digital */}
        <div className="mt-12 pt-6 border-t border-white/10 text-center">
          <p className="text-gray-500 text-xs">
            Desenvolvido por{' '}
            <a 
              href="https://grooverdigital.com.br" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-kairos-orange hover:text-kairos-orange-light transition-colors font-medium"
            >
              Groover Digital e Tecnologia
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
